import { ref, onMounted, computed, getCurrentInstance, watch } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'
import apiUseCase from '@/usecase/apiUseCase'
import returnUrlPrams from '@/utils/helpers/returnUrlPrams'

export function useUnpaidTransactions() {
  const store = useStore()
  const app = getCurrentInstance()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { $toast } = app!.appContext.config.globalProperties

  // State
  const filterDate = ref<Date[]>([])
  const searchQuery = ref('')
  const isLoading = ref(false)
  const dataSource = ref<any[]>([])
  const lastUpdated = ref<string>('')

  // Checkbox Selection State
  const selectedTransactionIds = ref<Set<string>>(new Set())
  const selectedTransactionsMap = ref<Map<string, number>>(new Map())

  // Filter States
  const appliedPartnerIds = ref<string[]>([])
  const appliedTransactionTypes = ref<string[]>([])

  // Options Data
  const partnerOptions = ref<any[]>([])
  const transactionTypeOptions = ref<any[]>([])

  const fetchFilterOptions = async () => {
    try {
      const [partnerRes, txTypeRes] = await Promise.all([
        apiUseCase.get('/management/v2/FuelStationPartners', '?pageSize=100&pageNumber=1'),
        apiUseCase.get('/transaction/v2/FuelingTransactions/TransactionTypes')
      ])

      if (!partnerRes.error) partnerOptions.value = partnerRes.result || []
      if (!txTypeRes.error) transactionTypeOptions.value = txTypeRes.result || []
    } catch (err) {
      console.error('Error fetching filter options', err)
    }
  }

  const fetchData = async () => {
    isLoading.value = true
    store.dispatch('showLoading')

    const customFilters: string[][] = [
      ['approvalStatuses', '=', 'Waiting'],
      ['approvalStatuses', '=', 'Submitted'],
      ['bypassPagination', '=', 'true']
    ]

    // Check if range date is selected properly
    if (filterDate.value && filterDate.value.length > 0) {
      const start = filterDate.value[0]
      const end = filterDate.value[1] || filterDate.value[0]
      if (start) {
        customFilters.push(['startDate', '=', moment(start as any).format('YYYY-MM-DD')])
        customFilters.push(['endDate', '=', moment(end as any).format('YYYY-MM-DD')])
      }
    }

    // Add checkbox filters using custom
    if (appliedPartnerIds.value.length > 0) {
      appliedPartnerIds.value.forEach((val) => {
        customFilters.push(['fuelStationPartnerIds', '=', val])
      })
    }

    if (appliedTransactionTypes.value.length > 0) {
      appliedTransactionTypes.value.forEach((val) => {
        customFilters.push(['transactionTypes', '=', val])
      })
    }

    const queryObj: any = {
      search: searchQuery.value,
      filterField: ['q'],
      custom: customFilters,
      pageSize: 0,
      pageNumber: 0
    }

    const queryStr = returnUrlPrams(queryObj)
    const endpoint = '/transaction/v2/FuelingTransactions'

    try {
      const response = await apiUseCase.get(endpoint, queryStr)
      if (!response.error) {
        const rawData = response.result || []
        rawData.sort((a: any, b: any) => {
          if (a.ApprovalStatus === 'Submitted' && b.ApprovalStatus !== 'Submitted') return 1
          if (a.ApprovalStatus !== 'Submitted' && b.ApprovalStatus === 'Submitted') return -1
          return 0
        })
        dataSource.value = rawData
        // Sync prices of selected items with newly fetched data
        rawData.forEach((item: any) => {
          if (selectedTransactionsMap.value.has(item.Id)) {
            selectedTransactionsMap.value.set(item.Id, item.Price || 0)
          }
        })
        lastUpdated.value = moment().format('DD MMMM YYYY - HH:mm')
      } else {
        $toast.add({ severity: 'error', detail: response.message, group: 'bc', life: 3000 })
      }
    } catch (err) {
      console.error(err)
      $toast.add({
        severity: 'error',
        summary: 'Gagal memuat data. Silahkan coba kembali beberapa saat lagi.',
        detail: err,
        group: 'bc',
        closable: false,
        life: 3000
      })
    } finally {
      isLoading.value = false
      store.dispatch('hideLoading')
    }
  }

  const activeFilters = computed(() => {
    const list: Array<{ id: string; category: string; value: any; label: string }> = []

    // Partner IDs
    appliedPartnerIds.value.forEach((val) => {
      const opt = partnerOptions.value.find((o) => o.Id === val)
      list.push({
        id: `partner_${val}`,
        category: 'partner',
        value: val,
        label: opt ? opt.Name : val
      })
    })

    // Transaction Types
    appliedTransactionTypes.value.forEach((val) => {
      const opt = transactionTypeOptions.value.find((o) => o.Name === val)
      list.push({
        id: `txType_${val}`,
        category: 'transactionType',
        value: val,
        label: opt ? opt.Description : val
      })
    })

    return list
  })

  const removeFilter = (filterItem: { category: string; value: any }) => {
    if (filterItem.category === 'partner') {
      appliedPartnerIds.value = appliedPartnerIds.value.filter((v) => v !== filterItem.value)
    } else if (filterItem.category === 'transactionType') {
      appliedTransactionTypes.value = appliedTransactionTypes.value.filter((v) => v !== filterItem.value)
    }
    fetchData()
  }

  const clearAllFilters = () => {
    filterDate.value = []
    searchQuery.value = ''
    appliedPartnerIds.value = []
    appliedTransactionTypes.value = []
    fetchData()
  }

  watch(
    () => filterDate.value,
    (newVal) => {
      if (!newVal || newVal.length === 0 || (newVal.length > 0 && !newVal[0] && !newVal[1])) {
        fetchData()
        return
      }
      const start = newVal[0]
      const end = newVal[1]

      if (start && end) {
        fetchData()
      }
    },
    { deep: true }
  )

  // Selection Logic
  const toggleSelectTransaction = (id: string) => {
    if (selectedTransactionsMap.value.has(id)) {
      selectedTransactionsMap.value.delete(id)
      selectedTransactionIds.value.delete(id)
    } else {
      const target = dataSource.value.find((item: any) => item.Id === id)
      const price = target ? (target.Price || 0) : 0
      selectedTransactionsMap.value.set(id, price)
      selectedTransactionIds.value.add(id)
    }
  }

  const selectableTransactions = computed(() => dataSource.value.filter((item: any) => item.ApprovalStatus === 'Waiting'))

  const isAllSelected = computed(() => selectableTransactions.value.length > 0 && selectableTransactions.value.every((item: any) => selectedTransactionIds.value.has(item.Id)))

  const toggleSelectAll = () => {
    if (isAllSelected.value) {
      selectableTransactions.value.forEach((item: any) => {
        selectedTransactionsMap.value.delete(item.Id)
        selectedTransactionIds.value.delete(item.Id)
      })
    } else {
      selectableTransactions.value.forEach((item: any) => {
        selectedTransactionsMap.value.set(item.Id, item.Price || 0)
        selectedTransactionIds.value.add(item.Id)
      })
    }
  }

  const selectedCount = computed(() => selectedTransactionIds.value.size)

  const selectedTotalAmount = computed(() => {
    let total = 0
    selectedTransactionsMap.value.forEach((price) => {
      total += price
    })
    return total
  })

  const resetSelection = () => {
    selectedTransactionsMap.value.clear()
    selectedTransactionIds.value.clear()
  }

  onMounted(() => {
    fetchFilterOptions()
    fetchData()
  })

  return {
    filterDate,
    searchQuery,
    isLoading,
    dataSource,
    lastUpdated,
    selectedTransactionIds,
    appliedPartnerIds,
    appliedTransactionTypes,
    partnerOptions,
    transactionTypeOptions,
    fetchData,
    activeFilters,
    removeFilter,
    clearAllFilters,
    toggleSelectTransaction,
    toggleSelectAll,
    isAllSelected,
    selectedCount,
    selectedTotalAmount,
    resetSelection
  }
}
